import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import { closeModal } from '../actions/modal';
import { SelectField } from '../containers/Form';
import Wizard from '../containers/Modal/Wizard';
import Strings from '../Strings';
import { fileActions } from '../actions/files';
import { DEVICE_INSTRUCTION_VIDEO_PURPOSE } from '../pages/Organizations/Configuration/deviceInstructionActions';
import { validateVideoFile } from '../pages/Organizations/Configuration/deviceInstructionHelpers';
import './FileModal.scss';

export const ADD_FILE_MODAL_NAME = 'add-file';

const isSuccess = response => response?.type?.endsWith('/result');
const responseError = (response, fallback) => response?.response?.data?.error?.message || fallback;

function AddFileModal({ data, onCancel, createUpload, completeUpload }) {
  const [purpose, setPurpose] = React.useState('');
  const [file, setFile] = React.useState(null);
  const [fileError, setFileError] = React.useState('');
  const [localURL, setLocalURL] = React.useState(null);
  const [submitError, setSubmitError] = React.useState('');
  const [validationErrors, setValidationErrors] = React.useState([]);
  const [highlightInvalidFields, setHighlightInvalidFields] = React.useState(false);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    if (!file) {
      setLocalURL(null);
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setLocalURL(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  React.useEffect(() => {
    const errors = [];
    if (!purpose) errors.push({ property: 'purpose', propertyName: Strings.deviceInstructionPurpose, missing: true });
    if (purpose && !file) errors.push({ property: 'file', propertyName: Strings.deviceInstructionChooseFile, missing: true });
    if (fileError) errors.push({ property: 'file', propertyName: Strings.deviceInstructionChooseFile, errors: [fileError], missing: false });
    setValidationErrors(errors);
  }, [purpose, file, fileError]);

  const chooseFile = event => {
    const chosen = event.target.files?.[0];
    if (!chosen) return;
    const error = validateVideoFile(chosen);
    setFileError(error);
    setFile(error ? null : chosen);
    setSubmitError('');
  };

  const onSubmit = async () => {
    if (isSaving || purpose !== DEVICE_INSTRUCTION_VIDEO_PURPOSE || !file || fileError) return;
    setIsSaving(true);
    setSubmitError('');
    try {
      const upload = await createUpload(file, purpose);
      if (!isSuccess(upload) || !upload.response?.upload_url || !upload.response?.object_name || !upload.response?.required_headers) {
        setSubmitError(responseError(upload, Strings.deviceInstructionUploadError));
        return;
      }
      const put = await fetch(upload.response.upload_url, {
        method: 'PUT',
        headers: upload.response.required_headers,
        body: file,
      });
      if (!put.ok) {
        setSubmitError(put.status === 412 ? Strings.deviceInstructionFileAlreadyExists : Strings.deviceInstructionUploadError);
        return;
      }
      const completed = await completeUpload(purpose, upload.response.object_name);
      if (!isSuccess(completed)) {
        setSubmitError(responseError(completed, Strings.deviceInstructionCompleteUploadError));
        return;
      }
      await data.onUploaded();
      onCancel();
    } catch (error) {
      setSubmitError(Strings.deviceInstructionUploadError);
    } finally {
      setIsSaving(false);
    }
  };

  const pages = [{
    id: 'add-file-page',
    title: Strings.deviceInstructionAddFile,
    content: (
      <>
        <SelectField
          id="device-instruction-purpose"
          name="device-instruction-purpose"
          label={Strings.deviceInstructionPurpose}
          placeholder={Strings.deviceInstructionSelectPurpose}
          value={purpose}
          onChange={option => {
            setPurpose(option?.value || '');
            setFile(null);
            setFileError('');
            setSubmitError('');
          }}
          data={[{
            value: DEVICE_INSTRUCTION_VIDEO_PURPOSE,
            label: Strings.deviceInstructionVideoPurpose,
            tooltip: Strings.deviceInstructionHelp,
            optionClassName: 'add-file-purpose-option',
          }]}
          isRequired
          highlightInvalid={highlightInvalidFields}
        />
        {purpose && (
          <>
            <span className="row">
              <label htmlFor="device-instruction-file" className="required-asterisk">
                {Strings.deviceInstructionChooseFile}
              </label>
              <input
                id="device-instruction-file"
                name="device-instruction-file"
                className={`file-modal__file-input ${highlightInvalidFields && (!file || fileError) ? 'invalid-control-background' : ''}`}
                type="file"
                accept=".mp4,.webm,video/mp4,video/webm"
                onChange={chooseFile}
                data-testid="field[device-instruction-file]"
              />
            </span>
            {fileError && <p role="alert">{fileError}</p>}
            {localURL && <video controls src={localURL} aria-label={Strings.formatString(Strings.deviceInstructionLocalPreview, file.name)} />}
          </>
        )}
        {submitError && <p role="alert">{submitError}</p>}
      </>
    ),
    nextButton: { text: isSaving ? Strings.deviceInstructionUploading : Strings.deviceInstructionUploadFile },
    emptyFieldsCount: validationErrors.filter(error => error.missing).length,
    canGoNext: !isSaving && purpose === DEVICE_INSTRUCTION_VIDEO_PURPOSE && Boolean(file) && validationErrors.length === 0,
  }];

  return <Wizard name={ADD_FILE_MODAL_NAME} pages={pages} onSubmit={onSubmit} onNextButtonHover={setHighlightInvalidFields} />;
}

AddFileModal.propTypes = {
  data: PropTypes.shape({ onUploaded: PropTypes.func.isRequired }).isRequired,
  onCancel: PropTypes.func.isRequired,
  createUpload: PropTypes.func.isRequired,
  completeUpload: PropTypes.func.isRequired,
};

const mapDispatchToProps = dispatch => ({
  onCancel: () => dispatch(closeModal(ADD_FILE_MODAL_NAME)),
  createUpload: (file, purpose) => dispatch(fileActions.createUpload(file, purpose)),
  completeUpload: (purpose, objectName) => dispatch(fileActions.completeUpload(purpose, objectName)),
});

export default connect(null, mapDispatchToProps)(AddFileModal);
